import { useEffect, useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import DescriptionIcon from '@mui/icons-material/Description'
import { useCreateComponentMutation, useGetAuthoringComponentQuery, useListComponentsQuery } from '../api/authoring'
import type { ComponentSummary, Document, Manifest } from '../api/authoring'
import { problemMessage } from '../api/baseApi'
import { isImage, thumbnail } from '../model/derived'
import { aOrAn, LISTING_LAG } from '../model/labels'
import { holdsFilesElsewhere, typeProse, uploadingFields } from '../model/manifest'
import { BlobUploader } from './BlobUploader'
import { CreateComponentDialog } from './CreateComponentDialog'
import { Loading, QueryError } from './QueryState'
import { shown } from '../api/useIntent'

/**
 * Choosing a picture, or putting a new one in, from wherever the picture
 * is needed.
 *
 * **"There is no photo here" and "I want to upload one" are one
 * moment.** The library and the three-step uploader both existed and
 * neither was reachable from the block being edited: an author who
 * wanted a new image had to leave the page, find the library, create a
 * Media, upload to it, come back, and place it. Every one of those steps
 * is real in the model and none of them is a decision the author was
 * making.
 *
 * So the two halves are one dialog. Choosing an existing one is a grid of
 * what the tenant holds; adding one creates the component and shows the
 * uploader against it immediately, because an upload is presigned
 * against a component that already exists and there is no way around
 * that — only a way to stop making the author perform it.
 *
 * **The type is a parameter, not a name.** A caller passes whichever type
 * the thing being edited accepts, which the manifest told it; this
 * dialog knows only that components of that type hold a file.
 */
export function MediaPicker({
  tenant,
  open,
  typeName,
  manifest,
  onClose,
  onChoose,
  busy,
  chosen,
}: {
  tenant: string
  open: boolean
  /** The type being chosen — one whose components hold a file. */
  typeName: string
  manifest: Manifest | undefined
  onClose: () => void
  /** The chosen component. A caller places it; this dialog never does. */
  onChoose: (component: Document | ComponentSummary) => void
  busy?: boolean
  /**
   * What is already chosen, where this dialog is being used to change
   * something rather than to fill something empty.
   *
   * **A picker that cannot show the current answer is asking a question
   * it has already been told the answer to.** Without it, replacing a
   * picture meant remembering which of a dozen thumbnails was the one
   * being replaced, and the only way to check was to cancel.
   */
  chosen?: string
}) {
  const [create, { isLoading: creating, error: createError, reset }] = useCreateComponentMutation()
  // The component an upload is being made against. It exists from the
  // moment it is created, which is why this is a document rather than a
  // flag: a create that is followed by a failed upload leaves something
  // real, and the author can still place it or try the file again.
  const [made, setMade] = useState<Document | null>(null)
  // The other way to have one: a file that lives somewhere else and is
  // pointed at. It is a document stated by its fields, so it is made by
  // the form every other create uses rather than by an upload.
  const [stating, setStating] = useState(false)

  // One entry per page fetched, oldest first, as the library keeps them:
  // each page stays its own cache entry keyed by the cursor that produced
  // it. A tenant's pictures outgrow one page long before they outgrow a
  // reason to pick an old one, and a picker that showed only the first
  // page made everything after it unreachable from the words.
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined])

  useEffect(() => {
    if (open) {
      setMade(null)
      reset()
      setCursors([undefined])
    }
  }, [open, reset, typeName])

  const prose = typeProse(manifest, typeName)
  // One array for the life of the type, because the create dialog resets
  // its form whenever it is handed a new one.
  const types = useMemo(() => [typeName], [typeName])

  // Re-read while an upload is in flight, so the tile below shows the
  // file the moment finalise answers. The finalise writes the whole
  // document into the cache itself, so this is a subscription rather
  // than a poll.
  const uploaded = useGetAuthoringComponentQuery({ tenant, id: made?.id ?? '' }, { skip: !made })

  const add = async () => {
    const created = await create({
      tenant,
      createComponent: { type: typeName, fields: uploadingFields(manifest, typeName) },
    }).unwrap()
    setMade(created)
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {made
          ? `Upload the file`
          : chosen
            ? `Choose a different ${prose.title.toLowerCase()}`
            : `Choose ${aOrAn(prose.title.toLowerCase())}`}
      </DialogTitle>
      <DialogContent dividers>
        {made ? (
          <Stack spacing={2}>
            <Alert severity="info">
              Now choose the file for this {prose.title.toLowerCase()}.
            </Alert>
            <BlobUploader tenant={tenant} id={made.id} document={uploaded.data ?? made} disabled={Boolean(busy)} />
          </Stack>
        ) : (
          <Stack spacing={2}>
            {cursors.map((cursor, index) => (
              <HeldPage
                key={cursor ?? ''}
                tenant={tenant}
                typeName={typeName}
                cursor={cursor}
                first={index === 0}
                last={index === cursors.length - 1}
                chosen={chosen}
                onChoose={onChoose}
                onMore={(next) => setCursors([...cursors, next])}
              />
            ))}

            <Typography variant="caption" color="text.secondary">
              {LISTING_LAG}
            </Typography>

            {createError && (
              <Alert severity="error">{problemMessage(createError, 'It could not be added.')}</Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {made ? (
          <Button variant="contained" onClick={() => onChoose(uploaded.data ?? made)}>
            Use it
          </Button>
        ) : (
          <>
            {holdsFilesElsewhere(manifest, typeName) && (
              <Button onClick={() => setStating(true)} disabled={creating || busy}>
                Use one hosted elsewhere
              </Button>
            )}
            <Button variant="contained" onClick={() => add().catch(shown)} disabled={creating || busy}>
              Upload a new one
            </Button>
          </>
        )}
      </DialogActions>
      <CreateComponentDialog
        tenant={tenant}
        open={stating}
        manifest={manifest}
        types={types}
        onClose={() => setStating(false)}
        onCreated={(created) => {
          setStating(false)
          onChoose(created)
        }}
      />
    </Dialog>
  )
}

/**
 * One page of what the tenant holds of the type, and the way on to the
 * next where there is one.
 */
function HeldPage({
  tenant,
  typeName,
  cursor,
  first,
  last,
  chosen,
  onChoose,
  onMore,
}: {
  tenant: string
  typeName: string
  cursor: string | undefined
  first: boolean
  last: boolean
  chosen?: string
  onChoose: (component: Document | ComponentSummary) => void
  onMore: (cursor: string) => void
}) {
  const listing = useListComponentsQuery({ tenant, type: typeName, limit: PAGE, ...(cursor ? { cursor } : {}) })
  const held = listing.data?.components ?? []

  if (listing.isLoading) return <Loading label="Looking…" />
  if (listing.error) return <QueryError error={listing.error} title="Could not look that up" />

  if (first && held.length === 0 && !listing.data?.cursor) {
    return <Alert severity="info">Nothing has been uploaded yet. Add one and it will be used here.</Alert>
  }

  return (
    <>
      {held.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: 2,
          }}
        >
          {held.map((component) => (
            <Tile
              key={component.id}
              tenant={tenant}
              component={component}
              current={component.id === chosen}
              onChoose={onChoose}
            />
          ))}
        </Box>
      )}
      {last && listing.data?.cursor && (
        <Stack direction="row">
          <Button onClick={() => onMore(listing.data!.cursor!)}>Load more</Button>
        </Stack>
      )}
    </>
  )
}

/**
 * Rows per page. Every tile is a document read — see `Tile` — so this is
 * what bounds the fan-out of opening the picker, and Load more is how an
 * author reaches past it.
 */
const PAGE = 48

/**
 * One choosable component, as a picture where there is one.
 *
 * It reads the document, which the listing row cannot supply: which
 * renditions exist is a fact the *document* carries, because the
 * platform skips a width above the original and makes none at all for
 * something that is not an image. `PAGE` is what bounds the
 * fan-out, and the reads are cached by `(tenant, id)` so opening one
 * afterwards costs nothing.
 */
function Tile({
  tenant,
  component,
  onChoose,
  current,
}: {
  tenant: string
  component: ComponentSummary
  /**
   * Handed the document once it is read, and the listing row only before.
   * A caller placing the picture reads its fields — the description the
   * library holds for it seeds a picture's `alt` — and a listing row has
   * none, so passing the row made every inserted picture undescribed.
   */
  onChoose: (component: Document | ComponentSummary) => void
  /** Whether this is the one already in use where the picker was opened. */
  current?: boolean
}) {
  const { data } = useGetAuthoringComponentQuery({ tenant, id: component.id })
  const preview = thumbnail(data, 320)
  const drawable = preview && isImage(data)

  return (
    <Paper
      variant="outlined"
      onClick={() => onChoose(data ?? component)}
      sx={{
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        ...(current
          ? { borderColor: 'primary.main', borderWidth: 2, boxShadow: 3 }
          : { '&:hover': { borderColor: 'primary.main' } }),
      }}
    >
      {current && (
        <Chip
          size="small"
          color="primary"
          label="In use here"
          sx={{ position: 'absolute', top: 6, left: 6, zIndex: 1 }}
        />
      )}
      <Box
        sx={{
          height: 110,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          color: 'text.disabled',
        }}
      >
        {drawable ? (
          <Box
            component="img"
            src={preview}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <DescriptionIcon fontSize="large" />
        )}
      </Box>
      <Typography variant="caption" noWrap sx={{ display: 'block', px: 1, py: 0.75 }}>
        {component.label ?? component.id}
      </Typography>
    </Paper>
  )
}
